// Locations store module
// ---
//
// ------------------------------------------------------------------------- /
import Vue from 'vue';
import get from 'lodash/get';
import set from 'lodash/set';

import apiService from '@/services/api';
import { ITEMS_PER_PAGE } from '@/services/constants';
import { downloadExcelFile, logAndToastError, resetState } from '../../utils';
import pupsMetadata from '@/assets/api/pups-filters-metadata.json';

// Helpers
// ------------------------------------------------------------------------- /
// Allowlisted pickup-point filters from the query string. The proxy turns these
// into OData; brand scope is applied there, not here.
function getPickupPointFilters() {
	const searchParams = new URLSearchParams(window.location.search);
	const filters = {};

	get(pupsMetadata, 'tableHeaders', []).forEach(({ name }) => {
		const values = searchParams
			.getAll(name)
			.reduce((all, param) => all.concat(param.split(',')), [])
			.filter(Boolean);

		if (values.length) {
			filters[name] = values;
		}
	});

	return filters;
}

function exportLocations(body, commit) {
	commit('setIsLoading', {
		isLoading: true,
	});

	const url = `locations/search/excel`;
	apiService
		.post(url, body)
		.then(response => {
			downloadExcelFile(response);
		})
		.catch(err => {
			logAndToastError(commit, 'Failed to export pickup points', err, {
				url,
				body,
			});
		})
		.finally(() => {
			commit('setIsLoading', {
				isLoading: false,
			});
		});
}

// Initial state
// ------------------------------------------------------------------------- /
const initialState = {
	items: {},
	locationsByRoles: {},
	locationsByBrand: {},
	locationsFilterList: [],
	isLoading: false,
};

// Getters
// ------------------------------------------------------------------------- /
const getters = {
	locationsLocation: state => locationId => get(state.items, locationId),
	locationsCompanyNumber: state => locationId => {
		const location = get(state, `items[${locationId}]`, undefined);
		return location?.companyNumber ?? undefined;
	},
	locationsCompanyNumberRequired: state => locationId => {
		const location = get(state, `items[${locationId}]`, undefined);
		return location?.companyNumberRequired ?? undefined;
	},
	locationsCompanyNumberFormat: state => locationId => {
		const location = get(state, `items[${locationId}]`, undefined);
		return location?.companyNumberFormat ?? undefined;
	},
	locationsByRoles: state => get(state, 'locationsByRoles.items', []),
	locationsByBrand: state => get(state, 'locationsByBrand.items', []),
	locationsByBrandCount: state => get(state, 'locationsByBrand.totalItems', 0),
	locationsFilterList: state => get(state, 'locationsFilterList', []),
	isLoading: state => state.isLoading,
	getAllowCustomPackaging: state => (locationId, brandId) => {
		const location = get(state, `items[${locationId}]`, undefined);

		return location?.contractPackagings?.find(({ brand }) => brand.id === brandId)?.allowCustomPackaging ?? false;
	},
	getPackagingOption: state => (locationId, brandId, batteryConditionId) => {
		const location = get(state, `items[${locationId}]`, undefined);

		return (
			location?.contractPackagings
				?.find(({ brand }) => brand.id === brandId)
				?.contractPackagingOption?.find(({ batteryCondition }) => batteryCondition.id === batteryConditionId)?.packagingOption?.name ?? null
		);
	},
};

// Actions
// ------------------------------------------------------------------------- /
const actions = {
	getLocation({ commit, state }, { locationId }) {
		if (!state.items[locationId]) {
			const url = `/locations/${locationId}`;
			apiService
				.get(url)
				.then(({ data: locationDescription }) => {
					commit('setLocation', { locationId, locationDescription });
				})
				.catch(err => {
					logAndToastError(commit, 'Failed to get location from the server', err, { url });
				});
		}
	},
	getLocationsByRoles({ commit }, { roleIds }) {
		// Collection organisation is not sent. POST /locations/searchforintegration
		// replaces the body on the proxy and sets linkedCOId from the session
		// (buildIntegrationLocationSearchBody).
		if (roleIds.length) {
			const url = `locations/searchforintegration?page=0&pageSize=${ITEMS_PER_PAGE.DEFAULT}`;
			const body = {
				businessRelationRoles: roleIds,
			};
			apiService
				.post(url, body)
				.then(({ data: locations }) => {
					commit('setLocationsByRoles', { locations });
				})
				.catch(err => {
					logAndToastError(commit, 'Failed to get locations by roles from the server', err, { url, body });
				});
		}
	},
	getLocationsByBrand({ commit }, { page = 0, excel = false }) {
		// Brand is not sent. The proxy sets linkedBrandId from the session
		// (buildPickupPointSearchBody) for both search and excel.
		const body = {
			filters: getPickupPointFilters(),
		};

		if (excel) {
			exportLocations(body, commit);
			return;
		}

		const url = `locations/search?page=${page}&pageSize=${ITEMS_PER_PAGE.DEFAULT}`;
		apiService
			.post(url, body)
			.then(({ data: locations }) => {
				commit('setLocationsByBrand', { locations });
			})
			.catch(err => {
				logAndToastError(commit, 'Failed to get locations by roles from the server', err, { url, body });
			});
	},
	getLocationsFilterList({ commit }, { businessRelationId }) {
		const url = `/businessrelations/${businessRelationId}/locations/selectlist`;
		apiService
			.get(url)
			.then(({ data: locations }) => {
				commit('setLocationsFilterList', { locations });
			})
			.catch(err => {
				logAndToastError(commit, this.$t('error.failedToGetLocationsFilterList'), err, { url });
			});
	},
	resetLocations({ commit }) {
		commit('resetLocations');
	},
	updateLocation({ commit, dispatch }, { locationId, companyNumber }) {
		const url = `/locations/${locationId}?companyNumber=${companyNumber}`;
		const body = {};
		commit('setIsLoading', { isLoading: true });
		apiService
			.put(url, body)
			.then(async () => {
				await commit('setLocation', { locationId, locationDescription: { companyNumber } });
				dispatch('revalidateLocations');
			})
			.catch(err => {
				logAndToastError(commit, 'Failed to update location', err, { url, body });
			})
			.finally(() => {
				commit('setIsLoading', { isLoading: false });
			});
	},
};

// Mutations
// ------------------------------------------------------------------------- /
const mutations = {
	setLocation(state, { locationId, locationDescription }) {
		Vue.set(state.items, locationId, locationDescription);
	},
	setLocationsByRoles(state, { locations }) {
		state.locationsByRoles = locations;
	},
	setLocationsByBrand(state, { locations }) {
		state.locationsByBrand = locations;
	},
	setLocationsFilterList(state, { locations }) {
		state.locationsFilterList = locations;
	},
	resetLocations(state) {
		resetState(state, initialState);
	},
	setIsLoading(state, { isLoading }) {
		set(state, 'isLoading', isLoading);
	},
};

export default {
	state: { ...initialState },
	getters,
	actions,
	mutations,
};
